import { useDisclosure } from "@/hooks";
import {
	useAnnounceRoomSelection,
	useAssignRoomsToMeditationRetreat,
	useGetRoomsByMeditationEvent,
} from "@/hooks/query-hooks/useRoom";
import { useNotificationStore } from "@/states/zustand/notification";
import {
	DeleteOutlined,
	ExclamationCircleOutlined,
	EyeOutlined,
	SoundOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Empty, Modal, Space, Tag } from "antd";
import { Link, useLocation } from "react-router";
import { PageTitle } from "../common";
import { RoomCard } from "../Rooms/RoomCard";
import { AssignRoomModal } from "./AssignRoomModal";

export function RoomsByMeditationRetreat({
	retreat,
}: {
	retreat: MeditationRetreat;
}) {
	const [open, { toggle }] = useDisclosure();
	const pathname = useLocation().pathname;

	const isInDashboard = pathname === "/";

	const { data: rooms = [] } = useGetRoomsByMeditationEvent(retreat.id, open);

	return (
		<>
			<Button
				icon={<EyeOutlined />}
				onClick={toggle}
				className="border-primary-gradient text-f80! rounded-lg!"
			>
				Rooms
			</Button>
			<Drawer
				open={open}
				width={640}
				title={<PageTitle>Rooms used for {retreat.title}</PageTitle>}
				onClose={toggle}
				extra={[
					!isInDashboard && (
						<AssignRoomModal
							key={"assign-room-modal"}
							retreat={retreat}
							assignedRooms={rooms || []}
						/>
					),
				]}
				footer={
					rooms?.length > 0 && (
						<AnnounceRoomSelectionAction
							key={"announce"}
							{...retreat}
						/>
					)
				}
				classNames={{
					footer: "flex justify-end!",
				}}
			>
				{rooms?.length > 0 ? (
					<Space
						direction="vertical"
						className="w-full!"
						size={"middle"}
					>
						<Space>
							<h2 className="text-base font-poppins">
								Total:
								<Tag color="success" className="ml-2!">
									{rooms?.length}
								</Tag>
							</h2>
						</Space>
						<section className="grid grid-cols-2 gap-5 w-full">
							{rooms.map((it) => {
								const { id } = it;

								return (
									<RoomCard
										key={it.id}
										{...it}
										actions={
											<div className="p-2 flex gap-2 justify-end ">
												<Link
													to={`/rooms/${id}/detail`}
												>
													<Button
														icon={
															<EyeOutlined className="text-f80!" />
														}
														type="primary"
														className="border-primary-gradient rounded-lg!"
														shape="circle"
														title="Room Detail"
														block
													/>
												</Link>
												{!isInDashboard && (
													<RemoveRoomAction
														selectedRoom={it}
														rooms={rooms}
														retreat={retreat}
													/>
												)}
											</div>
										}
									/>
								);
							})}
						</section>
					</Space>
				) : (
					<Empty
						description={"No rooms assigned to meditation event!"}
						className="font-poppins!"
					/>
				)}
			</Drawer>
		</>
	);
}

const RemoveRoomAction = ({
	selectedRoom,
	rooms,
	retreat,
}: {
	selectedRoom: Room;
	rooms: Room[];
	retreat: MeditationRetreat;
}) => {
	const [modal, contextHolder] = Modal.useModal();

	const { mutate } = useAssignRoomsToMeditationRetreat();
	const onRemove = () => {
		// have to remove the room from frontend side, cause the backend flow was sucked.
		// implementation => must have to filter the `selectedRoom` from the `rooms` to remove the `selectedRoom` and reAssign the remaining rooms as `roomIds` in payload so they don't get removed!

		const remainingRoomIds = rooms
			?.filter((it) => it.id !== selectedRoom.id)
			.map((it) => it.id);

		modal.confirm({
			icon: <ExclamationCircleOutlined className=" text-danger!" />,
			title: (
				<h2 className="font-poppins text-danger">
					Remove {selectedRoom.name} from {retreat.title}
				</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to remove this room from the
					meditation event - {retreat.title}
				</p>
			),
			onOk: () =>
				new Promise((resolve, reject) => {
					mutate(
						{ retreatId: retreat.id, roomIds: remainingRoomIds },
						{
							onSuccess: () =>
								resolve(
									useNotificationStore
										.getState()
										.setNotification({
											type: "success",
											message: `Remove ${selectedRoom.name} used for ${retreat.title} successfully!`,
										}),
								),
							onError: reject,
						},
					);
				}),
			okButtonProps: {
				danger: true,
				className: "rounded-lg!",
			},
			okText: "Remove",
			cancelButtonProps: {
				type: "text",
			},
		});
	};

	return (
		<>
			{contextHolder}
			<Button
				icon={<DeleteOutlined />}
				type="primary"
				danger
				onClick={onRemove}
				title="Remove Room"
				className="rounded-lg!"
			/>
		</>
	);
};

const AnnounceRoomSelectionAction = ({ id, title }: MeditationRetreat) => {
	const [modal, contextHolder] = Modal.useModal();

	const { mutate } = useAnnounceRoomSelection();
	const onAnnounce = () => {
		modal.confirm({
			icon: <ExclamationCircleOutlined className=" text-blue-500!" />,
			title: (
				<h2 className="font-poppins">Announce for room selection?</h2>
			),
			content: (
				<p className="font-poppins">
					Are you sure you want to announce & open the room selection
					for the meditation event - {title}
				</p>
			),
			onOk: () =>
				new Promise((resolve, reject) => {
					mutate(id, {
						onSuccess: resolve,
						onError: reject,
					});
				}),
			okButtonProps: {
				className: "rounded-lg! bg-primary-gradient",
			},
			okText: "Confirm",
			cancelButtonProps: {
				type: "text",
			},
		});
	};

	return (
		<>
			{contextHolder}
			<Button
				onClick={onAnnounce}
				type="primary"
				className="bg-primary-gradient rounded-lg!"
				size="large"
				icon={<SoundOutlined />}
				block
			>
				Make an announcement for Room Selection
			</Button>
		</>
	);
};
